/** Wrapper recipes supply picker labels and default sizes. Assets come from the CMS. */

import type { TemplateNode } from './types';

// Static generated index (not import.meta.glob — the bench bundles this
// library with esbuild, where Vite globs don't exist).
import { WRAPPER_ARTIFACTS } from './wrappers/wrappers.gen';
import componentMaterialsJson from './wrappers/component-materials.json';
import componentMetricsJson from './wrappers/component-metrics.json';

/**
 * Per-GLB mesh-order material names (Renderer.targetMaterialIndex addresses
 * meshes in load order). Generated by scripts/extract-prototype-wrappers.mjs
 * from the design-set GLBs' own node walks; the names are the 7 material
 * roles — the design sets author them as their material ids.
 */
export const COMPONENT_MATERIALS = componentMaterialsJson as Record<string, (string | null)[]>;

export interface ComponentMetrics {
    /** Rest extents [w, h, d] meters (accessor bounds, morphs at zero). */
    rest: [number, number, number];
    morphs: Record<'X' | 'Y' | 'Z', { in: boolean; out: boolean }>;
}

/**
 * Per-GLB rest size + morph capability (generated at extraction). Drives the
 * converter's shrink clamp: an axis with no In morphs cannot shrink by
 * geometry — the prototype's proxy-skin pipeline saturates near 0.7×rest
 * there (overlapping fins ARE its slat-wall look), and leaf-scale squeezing
 * below that visibly compresses the sculpted texture.
 */
export const COMPONENT_METRICS = componentMetricsJson as unknown as Record<string, ComponentMetrics>;

export interface WrapperSize {
    tree: TemplateNode;
    /** Path (node names, '/'-joined) from the root to the content mount; '' = the root itself. */
    mountPath: string;
    content: {
        /** Content box width the wrapper was designed around, du. */
        width: number | null;
        maxWidth: number | null;
        maxHeight: number | null;
        zooms: number[];
    };
}

export interface WrapperArtifact {
    kind: 'wrapper';
    id: string;
    /** base | backdrop | exhibition | frame | doodad | enclosure | composition */
    wrapperKind: string | null;
    label: string;
    description: string | null;
    notes: string | null;
    sizes: Record<string, WrapperSize>;
}

export const WRAPPER_LIBRARY: WrapperArtifact[] = (Object.values(WRAPPER_ARTIFACTS) as WrapperArtifact[])
    .sort((a, b) => a.id.localeCompare(b.id));

export function getWrapper(id: string): WrapperArtifact | null {
    return WRAPPER_LIBRARY.find(w => w.id === id || w.id === `display-${id}`) ?? null;
}

export function searchWrappers(query?: string): WrapperArtifact[] {
    if (!query?.trim()) return WRAPPER_LIBRARY;
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return WRAPPER_LIBRARY.filter(w => {
        const hay = [w.id, w.wrapperKind, w.label, w.description, w.notes].filter(Boolean).join(' ').toLowerCase();
        return terms.some(t => hay.includes(t));
    });
}

/** The preferred size for a wrapper: md when it exists, else the first. */
export function defaultSize(wrapper: WrapperArtifact): string {
    return wrapper.sizes.md ? 'md' : Object.keys(wrapper.sizes)[0];
}
